import Phaser from "phaser";
import { consume } from "../../api/consume";
import { loadMe, type Item, type ItemTemplateSummary, type PlayerFull } from "../../api/me";
import { move } from "../../api/movement";
import {
  CHUNK_SIZE,
  fetchChunk,
  globalToChunkCoord,
  globalToLocalCoord,
  interactTile,
  type WorldChunkResponse,
} from "../../api/world";
import type { TelegramContext } from "../../telegram";

const TILE_SIZE = 32;
const HUD_HEIGHT = 90;
const MOVE_ANIMATION_MS = 140;
const CONSUME_BTN_WIDTH = 200;
const CONSUME_BTN_HEIGHT = 32;

// Z-order layers (Phaser default depth = 0, większe = na wierzchu).
// Bez explicit depth elementy renderują się w kolejności dodawania.
// Problem 2A: emoji glyphs dodawane async w loadCurrentChunk() trafiały NAD
// synchronicznie utworzonym HUDem + player sprite. Rozwiązanie: depths.
const DEPTH_MAP = 0;
const DEPTH_OBJECTS = 10;
const DEPTH_PLAYER = 100;
const DEPTH_HUD = 1000;
const DEPTH_TOAST = 2000;

// Emoji glyph per category (placeholder do czasu real sprite assetów post-2C).
const CATEGORY_EMOJI: Record<string, string> = {
  tree: "🌳",
  bush: "🌿",
  stone: "🪨",
  water: "💧",
  cave_entrance: "🕳️",
  dungeon_entrance: "🚪",
  decoration: "🌼",
  special: "✨",
};

export class WorldScene extends Phaser.Scene {
  private playerFull!: PlayerFull;
  private telegram!: TelegramContext;

  // Phaser objects
  private mapGraphics!: Phaser.GameObjects.Graphics;
  private objectGlyphs: Phaser.GameObjects.Text[] = [];
  private playerSprite!: Phaser.GameObjects.Rectangle;
  private hudBg!: Phaser.GameObjects.Rectangle;
  private hudText!: Phaser.GameObjects.Text;
  private consumeBtnBg: Phaser.GameObjects.Rectangle | null = null;
  private consumeBtnText: Phaser.GameObjects.Text | null = null;
  private toastText: Phaser.GameObjects.Text | null = null;

  // State — playerX/Y to GLOBAL coords w świecie (Faza 2A).
  private playerX = 0;
  private playerY = 0;
  private isMoving = false;
  private currentStamina = 0;
  private currentHp = 0;
  private currentChunk: WorldChunkResponse | null = null;
  private currentChunkX = 0;
  private currentChunkY = 0;
  // Device pixel ratio — Text.setResolution(DPR) renderuje texture w fizycznej
  // rozdzielczości urządzenia, eliminuje rozmazanie na high-DPI mobile (Telegram WebApp).
  private dpr = 1;

  constructor() {
    super({ key: "WorldScene" });
  }

  create(): void {
    this.playerFull = this.registry.get("playerFull") as PlayerFull;
    this.telegram = this.registry.get("telegram") as TelegramContext;
    this.dpr = (this.registry.get("dpr") as number | undefined) ?? 1;

    const p = this.playerFull.player;
    this.playerX = p.currentPositionX;
    this.playerY = p.currentPositionY;
    this.currentStamina = p.currentStamina;
    this.currentHp = p.currentHp;
    this.currentChunkX = globalToChunkCoord(this.playerX);
    this.currentChunkY = globalToChunkCoord(this.playerY);

    this.cameras.main.setBackgroundColor("#1a2438");
    this.renderHUD();
    this.renderPlayer();
    this.renderConsumeButton();

    this.input.on("pointerdown", this.onTap, this);
    this.scale.on("resize", () => {
      this.relayoutHud();
      this.centerCameraOnPlayer();
    });

    this.centerCameraOnPlayer();

    // Async load chunka z DB (Faza 2A). Po renderze, gracz widzi obiekty.
    void this.loadCurrentChunk();
  }

  private async loadCurrentChunk(): Promise<void> {
    try {
      const chunk = await fetchChunk(this.currentChunkX, this.currentChunkY);
      this.currentChunk = chunk;
      this.renderMap();
    } catch (err) {
      console.error("[WorldScene.loadCurrentChunk]", err);
      // 404 = brak chunka. Pokazujemy "pusty rejon" placeholder + toast.
      this.currentChunk = null;
      this.renderMap();
      this.showToast(
        `Pusty rejon (${this.currentChunkX}, ${this.currentChunkY}). Edge regen w Fazie 2C.`,
      );
    }
  }

  private renderMap(): void {
    // Czyścimy poprzedni render (map graphics + emoji glyphs)
    if (this.mapGraphics) this.mapGraphics.destroy();
    for (const g of this.objectGlyphs) g.destroy();
    this.objectGlyphs = [];

    this.mapGraphics = this.add.graphics().setDepth(DEPTH_MAP);

    // Background chunka (z biome.backgroundColor lub fallback ciemny szary)
    const bgHex = this.currentChunk?.biome?.backgroundColor ?? "#3a3a3a";
    const bgInt = parseInt(bgHex.replace("#", ""), 16) || 0x3a3a3a;

    // Render tilemap origin = chunk top-left w world coords
    const originX = this.currentChunkX * CHUNK_SIZE * TILE_SIZE;
    const originY = this.currentChunkY * CHUNK_SIZE * TILE_SIZE;

    // Fill BG dla całego chunka
    this.mapGraphics.fillStyle(bgInt, 1);
    this.mapGraphics.fillRect(originX, originY, CHUNK_SIZE * TILE_SIZE, CHUNK_SIZE * TILE_SIZE);

    // Subtle grid lines (czarne, alpha 0.15) dla wizualnego rozdzielenia tile
    this.mapGraphics.lineStyle(1, 0x000000, 0.15);
    for (let i = 0; i <= CHUNK_SIZE; i++) {
      // pionowe
      this.mapGraphics.moveTo(originX + i * TILE_SIZE, originY);
      this.mapGraphics.lineTo(originX + i * TILE_SIZE, originY + CHUNK_SIZE * TILE_SIZE);
      // poziome
      this.mapGraphics.moveTo(originX, originY + i * TILE_SIZE);
      this.mapGraphics.lineTo(originX + CHUNK_SIZE * TILE_SIZE, originY + i * TILE_SIZE);
    }
    this.mapGraphics.strokePath();

    // Obiekty z tile_objects + lookup category przez templates → emoji glyph
    if (this.currentChunk) {
      const templateByCode = new Map(
        this.currentChunk.templates.map((t) => [t.code, t]),
      );
      const chunkGlobalX = this.currentChunk.chunk.chunkX * CHUNK_SIZE;
      const chunkGlobalY = this.currentChunk.chunk.chunkY * CHUNK_SIZE;

      for (const obj of this.currentChunk.objects) {
        const tpl = templateByCode.get(obj.templateCode);
        const category = tpl?.category ?? "decoration";
        const glyph = CATEGORY_EMOJI[category] ?? "❓";
        const worldX = (chunkGlobalX + obj.positionX) * TILE_SIZE + TILE_SIZE / 2;
        const worldY = (chunkGlobalY + obj.positionY) * TILE_SIZE + TILE_SIZE / 2;
        const text = this.add
          .text(worldX, worldY, glyph, {
            fontFamily: "sans-serif",
            fontSize: `${TILE_SIZE - 4}px`,
          })
          .setOrigin(0.5)
          .setDepth(DEPTH_OBJECTS)
          .setResolution(this.dpr);
        this.objectGlyphs.push(text);
      }
    }
  }

  private renderPlayer(): void {
    // Niebieski blok z bialym obwodem — placeholder sprite (AI-gen w iter 2)
    this.playerSprite = this.add.rectangle(
      this.playerX * TILE_SIZE + TILE_SIZE / 2,
      this.playerY * TILE_SIZE + TILE_SIZE / 2,
      TILE_SIZE - 8,
      TILE_SIZE - 8,
      0x4477ff,
    );
    this.playerSprite.setStrokeStyle(2, 0xffffff);
    this.playerSprite.setDepth(DEPTH_PLAYER);
  }

  private renderHUD(): void {
    // HUD fixed do kamery (scrollFactor 0 = nie scrolluje z wertikalna mapa)
    this.hudBg = this.add
      .rectangle(0, 0, this.scale.width, HUD_HEIGHT, 0x122738, 0.95)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(DEPTH_HUD);

    this.hudText = this.add
      .text(10, 10, this.formatHud(), {
        fontFamily: "ui-monospace, Menlo, monospace",
        fontSize: "12px",
        color: "#ffffff",
        lineSpacing: 5,
      })
      .setScrollFactor(0)
      .setDepth(DEPTH_HUD + 1)
      .setResolution(this.dpr);
  }

  private relayoutHud(): void {
    if (this.hudBg) {
      this.hudBg.width = this.scale.width;
    }
    this.repositionConsumeButton();
  }

  // === Consume button (prawy górny róg HUD) ===

  private findFirstConsumable(): { item: Item; tpl: ItemTemplateSummary } | null {
    for (const item of this.playerFull.inventory) {
      const tpl = this.playerFull.itemTemplatesUsed[item.templateId];
      if (tpl && tpl.category === "consumable") {
        return { item, tpl };
      }
    }
    return null;
  }

  private renderConsumeButton(): void {
    this.destroyConsumeButton();
    const c = this.findFirstConsumable();
    if (!c) return;

    const label = `Zjedz: ${c.tpl.namePl} x${c.item.stackCount}`;
    const x = this.scale.width - 10;
    const y = 10;

    this.consumeBtnBg = this.add
      .rectangle(x, y, CONSUME_BTN_WIDTH, CONSUME_BTN_HEIGHT, 0x11662d, 0.95)
      .setOrigin(1, 0)
      .setStrokeStyle(2, 0xffffff)
      .setScrollFactor(0)
      .setDepth(DEPTH_HUD + 2)
      .setInteractive({ useHandCursor: true });

    this.consumeBtnText = this.add
      .text(x - 10, y + 7, label, {
        fontFamily: "sans-serif",
        fontSize: "13px",
        color: "#ffffff",
      })
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setDepth(DEPTH_HUD + 3)
      .setResolution(this.dpr);

    this.consumeBtnBg.on(
      "pointerdown",
      (_pointer: Phaser.Input.Pointer, _x: number, _y: number, ev: Phaser.Types.Input.EventData) => {
        ev.stopPropagation();
        void this.onConsumeTap();
      },
    );
  }

  private destroyConsumeButton(): void {
    this.consumeBtnBg?.destroy();
    this.consumeBtnText?.destroy();
    this.consumeBtnBg = null;
    this.consumeBtnText = null;
  }

  private repositionConsumeButton(): void {
    if (!this.consumeBtnBg || !this.consumeBtnText) return;
    const x = this.scale.width - 10;
    const y = 10;
    this.consumeBtnBg.setPosition(x, y);
    this.consumeBtnText.setPosition(x - 10, y + 7);
  }

  private async onConsumeTap(): Promise<void> {
    const c = this.findFirstConsumable();
    if (!c) return;

    try {
      const result = await consume(c.item.id);
      if (!result.ok) {
        this.showToast(result.error, "error");
        return;
      }

      // Update stan klienta
      this.currentStamina = result.newStamina;
      this.currentHp = result.newHp;
      this.playerFull.player.currentStamina = result.newStamina;
      this.playerFull.player.currentHp = result.newHp;

      if (result.remainingStack === 0) {
        this.playerFull.inventory = this.playerFull.inventory.filter((it) => it.id !== c.item.id);
      } else {
        c.item.stackCount = result.remainingStack;
      }

      const parts: string[] = [];
      if (result.staminaRestored > 0) parts.push(`+${result.staminaRestored} STA`);
      if (result.hpRestored > 0) parts.push(`+${result.hpRestored} HP`);
      const detail = parts.length > 0 ? ` (${parts.join(", ")})` : "";
      this.showToast(`Zjadles: ${c.tpl.namePl}${detail}`, "success");

      this.updateHUD();
      this.renderConsumeButton(); // re-render dla nowego stackCount lub gdy item zniknal
    } catch (err) {
      this.showToast("Blad sieci przy jedzeniu", "error");
      console.error("[WorldScene.onConsumeTap]", err);
    }
  }

  private formatHud(): string {
    const p = this.playerFull.player;
    const staminaBar = this.bar(this.currentStamina, p.baseStats.stamina_max, 16);
    const hpBar = this.bar(this.currentHp, p.baseStats.hp, 16);
    const biomeName = this.currentChunk?.biome?.namePl ?? "—";
    const localX = this.playerX - this.currentChunkX * CHUNK_SIZE;
    const localY = this.playerY - this.currentChunkY * CHUNK_SIZE;
    return [
      `${p.displayName} (Lv ${p.level})   ${biomeName}`,
      `HP  ${hpBar} ${this.currentHp}/${p.baseStats.hp}`,
      `STA ${staminaBar} ${this.currentStamina}/${p.baseStats.stamina_max}`,
      `Chunk (${this.currentChunkX},${this.currentChunkY}) tile (${localX},${localY})   Gold: ${p.gold}   DPR: ${this.dpr}`,
    ].join("\n");
  }

  private bar(value: number, max: number, width: number): string {
    const filled = Math.round((value / max) * width);
    return "[" + "#".repeat(Math.max(0, filled)) + ".".repeat(Math.max(0, width - filled)) + "]";
  }

  private updateHUD(): void {
    this.hudText.setText(this.formatHud());
  }

  private centerCameraOnPlayer(): void {
    // Kamera centered na gracza, ale offset y zeby map nie zachodzila pod HUD
    this.cameras.main.centerOn(
      this.playerX * TILE_SIZE + TILE_SIZE / 2,
      this.playerY * TILE_SIZE + TILE_SIZE / 2,
    );
  }

  private async onTap(pointer: Phaser.Input.Pointer): Promise<void> {
    if (this.isMoving) return;

    // Tap w HUD area → ignore (zostawia obsluge przyciskom HUD np. Zjedz)
    if (pointer.y < HUD_HEIGHT) return;

    const targetTileX = Math.floor(pointer.worldX / TILE_SIZE);
    const targetTileY = Math.floor(pointer.worldY / TILE_SIZE);

    const dx = targetTileX - this.playerX;
    const dy = targetTileY - this.playerY;

    // MVP: tylko orthogonal +1/-1 step. W iter 2 — pathfinding (A*) na kafelek dalej.
    if (Math.abs(dx) + Math.abs(dy) !== 1) {
      this.showToast(dx === 0 && dy === 0 ? "To Twoja pozycja." : "Tap sasiedni kafelek (gora/dol/lewo/prawo).");
      return;
    }

    // Faza 2B: sprawdź czy target tile ma INTERACTIVE obiekt → interakcja zamiast ruchu.
    // Klik na interactive (drzewo/krzew/kamień) = harvest. Klik na empty / decoration = move.
    // Klik na non-walkable bez interactive → toast "zablokowane" (np. water).
    const targetChunkX = globalToChunkCoord(targetTileX);
    const targetChunkY = globalToChunkCoord(targetTileY);
    const targetLocalX = globalToLocalCoord(targetTileX);
    const targetLocalY = globalToLocalCoord(targetTileY);
    if (
      this.currentChunk &&
      targetChunkX === this.currentChunk.chunk.chunkX &&
      targetChunkY === this.currentChunk.chunk.chunkY
    ) {
      const targetObj = this.currentChunk.objects.find(
        (o) => o.positionX === targetLocalX && o.positionY === targetLocalY,
      );
      if (targetObj) {
        const tpl = this.currentChunk.templates.find((t) => t.code === targetObj.templateCode);
        if (tpl?.isInteractive === 1) {
          this.isMoving = true; // reuse flag dla mutex (nie pozwalamy multi-action)
          try {
            await this.onInteract(targetChunkX, targetChunkY, targetLocalX, targetLocalY);
          } finally {
            this.isMoving = false;
          }
          return;
        }
        if (tpl?.isWalkable === 0) {
          this.showToast("Pole zablokowane (drzewo/kamień). Klikaj sąsiednie aby zerwać.");
          return;
        }
      }
    }

    this.isMoving = true;
    try {
      const result = await move(dx as -1 | 0 | 1, dy as -1 | 0 | 1);
      if (!result.ok) {
        this.showToast(result.error);
        return;
      }

      // Optimistic update + animacja
      this.playerX = result.newX;
      this.playerY = result.newY;
      this.currentStamina = result.newStamina;

      // Faza 2A: jeśli gracz przekroczył granicę chunka → re-fetch + re-render
      const newChunkX = globalToChunkCoord(this.playerX);
      const newChunkY = globalToChunkCoord(this.playerY);
      if (newChunkX !== this.currentChunkX || newChunkY !== this.currentChunkY) {
        this.currentChunkX = newChunkX;
        this.currentChunkY = newChunkY;
        void this.loadCurrentChunk();
      }

      this.tweens.add({
        targets: this.playerSprite,
        x: this.playerX * TILE_SIZE + TILE_SIZE / 2,
        y: this.playerY * TILE_SIZE + TILE_SIZE / 2,
        duration: MOVE_ANIMATION_MS,
        ease: "Quad.easeOut",
        onUpdate: () => this.centerCameraOnPlayer(),
        onComplete: () => {
          this.updateHUD();
        },
      });
    } catch (err) {
      this.showToast("Blad sieci. Sprobuj za chwile.");
      console.error("[WorldScene.onTap]", err);
    } finally {
      this.isMoving = false;
    }
  }

  /** Faza 2B: interact z sąsiednim obiektem — drop loot lub miss + cooldown. */
  private async onInteract(
    chunkX: number,
    chunkY: number,
    positionX: number,
    positionY: number,
  ): Promise<void> {
    try {
      const result = await interactTile({ chunkX, chunkY, positionX, positionY });

      if (!result.ok) {
        // Mapping error codes na user-friendly toast
        if (result.code === "ON_COOLDOWN" && result.cooldownUntil) {
          const until = new Date(result.cooldownUntil);
          const mins = Math.max(1, Math.round((until.getTime() - Date.now()) / 60000));
          this.showToast(`Cooldown ${mins} min — wróć później.`);
        } else if (result.code === "INVENTORY_FULL") {
          this.showToast("Plecak pełny. Zjedz coś albo wyrzuć item.");
        } else if (result.code === "NO_STAMINA") {
          this.showToast("Brak staminy. Zjedz jabłko albo odpocznij.");
        } else {
          this.showToast(result.error);
        }
        return;
      }

      // Stamina drain
      this.currentStamina = result.newStamina;

      // Toast z drops
      if (result.drops.length === 0) {
        this.showToast("Nic nie wypadło. Spróbuj ponownie później.");
      } else {
        const dropText = result.drops.map((d) => `+${d.namePl} x${d.count}`).join(", ");
        this.showToast(dropText, "success");
      }

      // Refresh inventory — re-fetch /api/me i update registry żeby consume button widzialł nowy stack.
      // Cheap operation (~50ms), akceptowalne dla MVP. W przyszłości: optimistic patch.
      try {
        const fresh = await loadMe();
        this.playerFull = fresh;
        this.registry.set("playerFull", fresh);
        this.renderConsumeButton();
      } catch (err) {
        console.warn("[WorldScene.onInteract] refresh /api/me failed:", err);
      }

      this.updateHUD();
    } catch (err) {
      this.showToast("Blad sieci. Sprobuj za chwile.");
      console.error("[WorldScene.onInteract]", err);
    }
  }

  private showToast(msg: string, variant: "error" | "success" = "error"): void {
    if (this.toastText) {
      this.toastText.destroy();
    }
    const bg = variant === "success" ? "#11662d" : "#8b2e2e";
    this.toastText = this.add
      .text(this.scale.width / 2, this.scale.height - 30, msg, {
        fontFamily: "sans-serif",
        fontSize: "13px",
        color: "#ffffff",
        backgroundColor: bg,
        padding: { x: 10, y: 5 },
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(DEPTH_TOAST)
      .setResolution(this.dpr);

    this.tweens.add({
      targets: this.toastText,
      alpha: 0,
      delay: 2000,
      duration: 500,
      onComplete: () => {
        this.toastText?.destroy();
        this.toastText = null;
      },
    });
  }
}
